import { useState } from "react";

import { Box } from "ui/box";
import { PreviewSkeleton } from "containers/admin/clients/client/theme-configuration-page/theme-preview/preview-skeleton";

import { useElementSize } from "containers/admin/clients/client/theme-configuration-page/theme-preview/use-element-size";

import {
  FrameBox,
  PreviewIframe,
  STAGE_SX,
} from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.styles";
import {
  FRAME_BORDER_WIDTH,
  SCREEN_SIZE_WIDTHS,
  type ScreenSize,
} from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

type PreviewFrameProps = {
  title: string;
  /** `null` while the data the document is built from is still loading. */
  srcDoc: string | null;
  screenSize: ScreenSize;
};

const FRAME_BORDERS = 2 * FRAME_BORDER_WIDTH;

/**
 * A new `srcDoc` reloads an iframe, which stays blank until its CSS and fonts arrive. So the next document
 * loads in a hidden frame while the last loaded one stays on screen, and they swap on `load`; only the
 * very first load, with nothing to show yet, gets a skeleton.
 */
export const PreviewFrame = ({ title, srcDoc, screenSize }: PreviewFrameProps) => {
  const { ref, width, height } = useElementSize<HTMLDivElement>();
  const [loadedDoc, setLoadedDoc] = useState<string | null>(null);

  const documentWidth = SCREEN_SIZE_WIDTHS[screenSize];
  const availableWidth = width - FRAME_BORDERS;
  const scale = width > 0 && availableWidth < documentWidth ? availableWidth / documentWidth : 1;
  const isScaled = scale < 1;
  const isFluid = screenSize === "large" && !isScaled;

  const documents = [loadedDoc, srcDoc].filter(
    (document, index, all): document is string => !!document && all.indexOf(document) === index,
  );

  return (
    <Box ref={ref} sx={STAGE_SX}>
      <FrameBox
        frameWidth={isFluid || isScaled ? undefined : documentWidth + FRAME_BORDERS}
        isLoading={loadedDoc === null}>
        {documents.map((document) => {
          const isBuffering = document !== loadedDoc;

          return (
            <PreviewIframe
              key={document}
              title={isBuffering ? `${title} (loading)` : title}
              aria-hidden={isBuffering || undefined}
              tabIndex={isBuffering ? -1 : undefined}
              srcDoc={document}
              sandbox=""
              referrerPolicy="no-referrer"
              onLoad={document === srcDoc ? () => setLoadedDoc(document) : undefined}
              isBuffering={isBuffering}
              scale={scale}
              frameWidth={isScaled ? documentWidth : undefined}
              frameHeight={isScaled ? (height - FRAME_BORDERS) / scale : undefined}
              data-testid={isBuffering ? "theme-preview-buffer-iframe" : "theme-preview-iframe"}
            />
          );
        })}
        {loadedDoc === null && <PreviewSkeleton />}
      </FrameBox>
    </Box>
  );
};
